import { Trans } from '@lingui/react/macro';
import { DateDisplay } from '@/components/localization';
import { Tooltip } from '@/components/Tooltip';
import React from 'react';
import theme from './HistoryLog.module.scss';
import { HistoryLogActions } from './HistoryLogActions';
import { getHistoryLogComparisonTranslation } from './HistoryLogComparisonTranslationMap';
import { TextView } from '@ygtechnologies/bygr-frontend-component-library';

enum DataType {
    Unknown = 0,
    String,
    Number,
    Date,
    Bool,
}
export interface ComparisonObject {
    propertyName: string;
    oldValue: string;
    newValue: string;
    dataType: DataType;
}

interface HistoryLogComparisonProps {
    data: ComparisonObject[];
    operationKey: HistoryLogActions['All'];
    /** Will force table instead of tooltip, as long as data.length > 1
     * */
    forceTable?: boolean;
}

const HistoryLogComparison = ({ data, operationKey, forceTable }: HistoryLogComparisonProps) => {
    if (data.length === 0) return null;
    const renderValues = (d: ComparisonObject) => {
        if (d.dataType === DataType.Date) {
            return [<DateDisplay value={d.oldValue} />, <DateDisplay value={d.newValue} />];
        }
        if (d.dataType === DataType.Bool) {
            // Handle both boolean values and string representations
            const isTruthy = (val: unknown) => val === true || val === 'true';
            return [
                isTruthy(d.oldValue) ? <Trans>Yes</Trans> : <Trans>No</Trans>,
                isTruthy(d.newValue) ? <Trans>Yes</Trans> : <Trans>No</Trans>,
            ];
        }
        return [d.oldValue, d.newValue];
    };

    const generateComparisonTable = () => (
        <table className={theme.comparisonTable}>
            <thead>
                <tr>
                    <th>
                        <Trans>Name</Trans>
                    </th>
                    <th>
                        <Trans>Old</Trans>
                    </th>
                    <th>
                        <Trans>New</Trans>
                    </th>
                </tr>
            </thead>
            <tbody>
                {data.map((x) => {
                    const values = renderValues(x);
                    return (
                        <tr key={x.propertyName}>
                            <td>{getHistoryLogComparisonTranslation(operationKey, x.propertyName)}</td>
                            <td>{
                                <TextView numberOfLines={10}>
                                    {values[0]}
                                </TextView>
                            }</td>
                            <td>
                                <TextView numberOfLines={10}>
                                    {values[1]}
                                </TextView>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    if (data.length === 1 && (data[0].propertyName != 'TermsAndConditions' && data[0].propertyName != 'OptionsTermsText')) {
        const values = renderValues(data[0]);
        const translatedProperty = getHistoryLogComparisonTranslation(operationKey, data[0].propertyName);
        return (
            <span>
                <Trans>
                    "{translatedProperty}" changed from <b>{values[0]}</b> to <b>{values[1]}</b>
                </Trans>
            </span>
        );
    }
    if (forceTable) {
        return generateComparisonTable();
    }

    return (
        <Tooltip
            title={<Trans>Differences between old and new</Trans>}
            includeQuestionMark
            dynamicWidth
            minWidthPx={200}
            color="light"
            className={theme.tooltip}
            content={generateComparisonTable()}>
            <span>
                <Trans>View changes</Trans>
            </span>
        </Tooltip>
    );
};

export default HistoryLogComparison;
